import jsPDF from "jspdf";
import "jspdf-autotable";
import { getImpresionPresupuesto } from "../api/Estimates/impresionPresupuestoApi";
import { getFileContent } from "../api/Proyectos/projectFilesApi";
import { getEmpresaPdfByKey } from "../constants/a3EmpresaPdf";
import {
  fmtBudgetDate,
  fmtBudgetNum,
  fmtBudgetQty,
  getImageFormatFromDataUrl,
  isPdfExt,
  looksImageExt,
} from "./budgetPrintUtils";
import {
  createPrintPalette,
  drawInfoCard,
  drawNotesBlock,
  drawPageFooter,
  drawPartyBlock,
  drawPdfHeader,
  drawSectionTitle,
  loadPdfImage,
  normalizeBrandRgb,
} from "./stagePdfPrintLayout";

const LABELS = {
  budget: "Budget",
  budgetSummary: "Budget Summary",
  client: "Client",
  quotationDate: "Quotation date",
  styleName: "Style name",
  unitPrice: "Unit price",
  currency: "Currency",
  qty: "Qty",
  status: "Status",
  documentTotal: "Document total",
  lineItems: "Style",
  picture: "Picture",
  budgetNo: "Budget Nº",
  page: "Page",
};

const dash = (v) => {
  const s = String(v ?? "").trim();
  return s || "—";
};

/** Normaliza nombre de proveedor para matching. */
const normSupplier = (name) =>
  String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

/** Supplier NML TURKEY → cabecera NML SPAIN (logo nml.png). */
export const isNmlTurkeySupplier = (name) => {
  const n = normSupplier(name);
  return n.includes("NML") && n.includes("TURKEY");
};

/**
 * Empresa emisora del Budget PDF según supplier del presupuesto.
 * NML TURKEY → NML SPAIN + nml.png + NIF B06786768 (misma dirección Stage).
 */
export function resolveBudgetEmpresaPdf(header = {}, overrides = {}) {
  const supplier =
    overrides.proveedorNombre ||
    header.ProveedorNombre ||
    header.N_PROVEEDOR ||
    "";
  if (isNmlTurkeySupplier(supplier)) {
    const base = getEmpresaPdfByKey("NML_SPAIN");
    return {
      ...base,
      name: "NML SPAIN",
      vat: "B06786768",
    };
  }
  return getEmpresaPdfByKey("STAGE_DESIGN");
}

async function resolveBudgetImage(header, idPresupuesto) {
  try {
    let files = [];
    if (header?.NumeroPresupuesto) {
      files = await getFileContent(header.NumeroPresupuesto);
    }
    if ((!files || files.length === 0) && idPresupuesto) {
      files = await getFileContent(idPresupuesto);
    }
    if (!files?.length) return null;
    const firstImg = files.find(
      (f) =>
        f &&
        !isPdfExt(f.filePath || f.fileName || "") &&
        (f.mimeType?.startsWith("image/") ||
          looksImageExt(f.filePath || f.fileName || "")),
    );
    if (firstImg?.data && firstImg.mimeType) {
      return `data:${firstImg.mimeType};base64,${firstImg.data}`;
    }
  } catch (err) {
    console.error("[printBudgetPdf] imagen:", err);
  }
  return null;
}

/**
 * En escandallo, LineasPresupuestos.Venta es precio de VENTA UNITARIO (no el total doc).
 * totals.subTotal = suma de ventas unitarias; totals.total = subTotal * Units.
 */
function resolveUnitPrice(header, overrides = {}, totals = {}) {
  if (overrides.precioUnitario != null && overrides.precioUnitario !== "") {
    const n = Number(overrides.precioUnitario);
    if (Number.isFinite(n)) return n;
  }
  const sub = Number(totals.subTotal);
  if (Number.isFinite(sub) && sub > 0) return sub;
  const lineVenta = Number(header.Venta ?? header.VentaUN);
  if (Number.isFinite(lineVenta) && lineVenta > 0) return lineVenta;
  return 0;
}

function resolveQty(header, overrides = {}) {
  if (overrides.unidades != null && overrides.unidades !== "") {
    return Number(overrides.unidades);
  }
  return Number(header.Units ?? header.TotalUnits ?? 0) || 0;
}

function resolveDocumentTotal(
  header,
  overrides = {},
  totals = {},
  unitPrice,
  qty,
) {
  if (overrides.totalVenta != null && overrides.totalVenta !== "") {
    const n = Number(overrides.totalVenta);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const apiTotal = Number(totals.total);
  if (Number.isFinite(apiTotal) && apiTotal > 0) return apiTotal;
  if (Number.isFinite(unitPrice) && Number.isFinite(qty) && qty > 0) {
    return unitPrice * qty;
  }
  return null;
}

function resolveStyleName(header, overrides = {}) {
  return dash(
    overrides.nombreProducto ||
      header.designCliente ||
      header.DiseñoMuestra ||
      header.NombreProducto,
  );
}

function resolveCurrency(header, overrides = {}) {
  return dash(overrides.moneda || header.MonedaVenta || "EUR");
}

function resolveDocRef(header, idPresupuesto) {
  return dash(
    header?.Presupuesto ||
      header?.NumeroPresupuesto ||
      (idPresupuesto ? `#${idPresupuesto}` : "Budget"),
  );
}

function buildClientDetailLines(header) {
  const lines = [];
  const address = String(header?.address || "").trim();
  if (address) lines.push(address);
  const cityState = [header?.city, header?.state].filter(Boolean).join(", ");
  if (cityState) lines.push(cityState);
  if (header?.country) lines.push(String(header.country));
  if (header?.Estado) lines.push(`${LABELS.status}: ${header.Estado}`);
  return lines;
}

function applyFooters(doc, emp, palette, marginLeft, marginRight) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    drawPageFooter(
      doc,
      pageW,
      marginLeft,
      marginRight,
      pageH,
      i,
      emp,
      palette,
      LABELS.page,
    );
  }
}

/**
 * Dibuja una página de Budget con el mismo layout visual que Purchase Order.
 * La marca (Stage / NML SPAIN) se resuelve por supplier del presupuesto.
 * @returns {number} Y final tras el contenido
 */
async function drawBudgetPage(
  doc,
  { header, idPresupuesto, overrides = {}, totals = {}, pageStart = false },
) {
  if (pageStart) doc.addPage();

  const emp = resolveBudgetEmpresaPdf(header, overrides);
  const brandRgb = normalizeBrandRgb(emp.headerRgb);
  const palette = createPrintPalette(brandRgb);
  const logo = emp.logo ? await loadPdfImage(emp.logo).catch(() => null) : null;

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginLeft = 14;
  const marginRight = 14;
  const contentW = pageW - marginLeft - marginRight;

  const docRef = resolveDocRef(header, idPresupuesto);
  const styleName = resolveStyleName(header, overrides);
  const unitPrice = resolveUnitPrice(header, overrides, totals);
  const qty = resolveQty(header, overrides);
  const moneda = resolveCurrency(header, overrides);
  const quotationDate = fmtBudgetDate(
    overrides.fechaPresupuesto ||
      header?.FechaAceptacion ||
      header?.FechaPresentacion ||
      new Date(),
  );
  const totalDoc = resolveDocumentTotal(
    header,
    overrides,
    totals,
    unitPrice,
    qty,
  );

  let y = drawPdfHeader(doc, {
    pageW,
    marginLeft,
    marginRight,
    logo,
    emp,
    docTitle: LABELS.budget,
    docRef,
    docDate: fmtBudgetDate(new Date()),
    palette,
  });

  y = drawPartyBlock(doc, {
    marginLeft,
    contentW,
    y,
    title: LABELS.client,
    name: dash(header?.name),
    detailLines: buildClientDetailLines(header),
  });

  y = drawInfoCard(doc, {
    marginLeft,
    contentW,
    y,
    fields: [
      { label: LABELS.quotationDate, value: quotationDate },
      { label: LABELS.styleName, value: styleName },
      { label: LABELS.unitPrice, value: fmtBudgetNum(unitPrice) },
      { label: LABELS.currency, value: moneda },
      { label: LABELS.qty, value: fmtBudgetQty(qty) },
      Number.isFinite(totalDoc)
        ? {
            label: LABELS.documentTotal,
            value: fmtBudgetNum(totalDoc),
            highlight: true,
          }
        : null,
    ].filter(Boolean),
    palette,
    brandRgb,
    totalLabel: LABELS.documentTotal,
    totalSuffix: moneda === "—" ? "" : moneda,
  });

  const imgData = await resolveBudgetImage(header, idPresupuesto);

  drawSectionTitle(doc, {
    x: marginLeft,
    y: y + 0.5,
    title: LABELS.lineItems,
    rgb: palette.brand,
  });
  y += 6;

  const imgTargetH = 28;
  const imgTargetW = 28;
  const head = [
    [
      LABELS.picture,
      LABELS.quotationDate,
      LABELS.styleName,
      LABELS.unitPrice,
      LABELS.currency,
      LABELS.qty,
    ],
  ];
  const body = [
    [
      imgData ? "" : "—",
      quotationDate,
      styleName,
      fmtBudgetNum(unitPrice),
      moneda,
      fmtBudgetQty(qty),
    ],
  ];

  doc.autoTable({
    head,
    body,
    startY: y,
    theme: "plain",
    margin: { left: marginLeft, right: marginRight },
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 2.4, right: 2, bottom: 2.4, left: 2 },
      lineColor: palette.border,
      lineWidth: 0.15,
      textColor: [38, 44, 52],
      valign: "middle",
      halign: "center",
    },
    headStyles: {
      fillColor: palette.tableHead,
      textColor: palette.tableHeadText,
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: { top: 2.8, right: 2, bottom: 2.8, left: 2 },
    },
    bodyStyles: {
      minCellHeight: imgTargetH,
      fillColor: [255, 255, 255],
    },
    alternateRowStyles: {
      fillColor: palette.rowAlt,
    },
    columnStyles: {
      0: { cellWidth: imgTargetW + 4, halign: "center" },
      2: { halign: "left", cellWidth: 55 },
      3: { halign: "right" },
      5: { halign: "right" },
    },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 0 && imgData) {
        const cell = data.cell;
        const fmt = getImageFormatFromDataUrl(imgData);
        const imgX = cell.x + (cell.width - imgTargetW) / 2;
        const imgY = cell.y + (cell.height - imgTargetH) / 2;
        doc.addImage(imgData, fmt, imgX, imgY, imgTargetW, imgTargetH);
      }
    },
  });

  y = (doc.lastAutoTable?.finalY || y + 40) + 10;

  const notas = String(header?.Notas || "").trim();
  if (notas) {
    y = drawNotesBlock(doc, {
      marginLeft,
      contentW,
      y,
      notes: notas,
      palette,
    });
  }

  const pageNum = doc.internal.getNumberOfPages();
  drawPageFooter(
    doc,
    pageW,
    marginLeft,
    marginRight,
    pageH,
    pageNum,
    emp,
    palette,
    LABELS.page,
  );

  return y;
}

/**
 * Imprime un presupuesto individual (drawer Estimates).
 */
export async function printBudgetPdf({
  idPresupuesto,
  nombreProducto,
  precioUnitario,
  moneda,
  unidades,
  fechaPresupuesto,
  proveedorNombre,
  totalVenta,
  returnBlob = false,
} = {}) {
  if (!idPresupuesto) throw new Error("No hay presupuesto para imprimir");

  const data = await getImpresionPresupuesto(idPresupuesto);
  const details = Array.isArray(data.details) ? data.details : [];
  const header = details[0] || {};
  const totals = data.totals || {
    subTotal: details.reduce((s, l) => s + (parseFloat(l.Venta) || 0), 0),
    total: null,
  };
  if (totals.total == null && Number.isFinite(Number(totals.subTotal))) {
    const units = Number(unidades ?? header.TotalUnits ?? header.Units ?? 0);
    totals.total =
      units > 0 ? Number(totals.subTotal) * units : Number(totals.subTotal);
  }

  const doc = new jsPDF("p", "mm", "a4");

  await drawBudgetPage(doc, {
    header,
    idPresupuesto,
    totals,
    overrides: {
      nombreProducto,
      precioUnitario,
      moneda,
      unidades,
      fechaPresupuesto,
      proveedorNombre,
      totalVenta,
    },
  });

  const emp = resolveBudgetEmpresaPdf(header, { proveedorNombre });
  const safeRef = resolveDocRef(header, idPresupuesto).replace(
    /[/\\?%*:|"<>]/g,
    "-",
  );
  const slug = emp.key === "NML_SPAIN" ? "NML" : "Stage";
  const filename = `${slug}_Budget_${safeRef}.pdf`;
  if (returnBlob) {
    const blob = doc.output("blob");
    return new File([blob], filename, { type: "application/pdf" });
  }
  doc.save(filename);
  return null;
}

/**
 * Genera el PDF del presupuesto como File (para adjuntar a email).
 */
export async function buildBudgetPdfFile(args = {}) {
  return printBudgetPdf({ ...args, returnBlob: true });
}

export async function buildBudgetGroupedPdfFile(args = {}) {
  return printBudgetGroupedPdf({ ...args, returnBlob: true });
}

/**
 * Imprime varios presupuestos (una página cada uno).
 */
export async function printBudgetSelectionPdf({
  idsPresupuestos = [],
  proyectos = [],
} = {}) {
  if (!idsPresupuestos.length) return;

  const doc = new jsPDF("p", "mm", "a4");

  for (let i = 0; i < idsPresupuestos.length; i += 1) {
    const idPresupuesto = idsPresupuestos[i];
    let local = proyectos.find((p) => p.IdPresupuesto === idPresupuesto);
    let apiDetail = null;
    let totals = {};

    try {
      const apiData = await getImpresionPresupuesto(idPresupuesto);
      const details = Array.isArray(apiData.details) ? apiData.details : [];
      apiDetail = details[0] || {};
      totals = apiData.totals || {
        subTotal: details.reduce((s, l) => s + (parseFloat(l.Venta) || 0), 0),
      };
      if (totals.total == null && Number.isFinite(Number(totals.subTotal))) {
        const units = Number(
          local?.Units ?? apiDetail.TotalUnits ?? apiDetail.Units ?? 0,
        );
        totals.total =
          units > 0 ? Number(totals.subTotal) * units : Number(totals.subTotal);
      }
    } catch (err) {
      console.error("[printBudgetSelectionPdf] detalle:", err);
    }

    const header = { ...apiDetail, ...local };

    await drawBudgetPage(doc, {
      header,
      idPresupuesto,
      totals,
      overrides: {
        proveedorNombre: local?.ProveedorNombre || apiDetail?.ProveedorNombre,
        unidades: local?.Units,
        precioUnitario: local?.VentaUN,
        totalVenta: local?.Venta,
      },
      pageStart: i > 0,
    });
  }

  doc.save("Stage_Budget.pdf");
}

/**
 * Resumen agrupado de varios presupuestos (una tabla, mismo look de marca).
 */
export async function printBudgetGroupedPdf({
  idsPresupuestos = [],
  proyectos = [],
  returnBlob = false,
} = {}) {
  if (!idsPresupuestos.length) return;

  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const marginLeft = 14;
  const marginRight = 14;
  const contentW = pageW - marginLeft - marginRight;

  let firstBudget = proyectos.find(
    (p) => p.IdPresupuesto === idsPresupuestos[0],
  );
  let apiFirst = null;
  try {
    const apiData = await getImpresionPresupuesto(idsPresupuestos[0]);
    apiFirst = (apiData.details && apiData.details[0]) || {};
  } catch (err) {
    console.error("[printBudgetGroupedPdf] cliente:", err);
  }
  const header = { ...apiFirst, ...firstBudget };
  const emp = resolveBudgetEmpresaPdf(header, {
    proveedorNombre: firstBudget?.ProveedorNombre || apiFirst?.ProveedorNombre,
  });
  const brandRgb = normalizeBrandRgb(emp.headerRgb);
  const palette = createPrintPalette(brandRgb);
  const logo = emp.logo ? await loadPdfImage(emp.logo).catch(() => null) : null;

  let y = drawPdfHeader(doc, {
    pageW,
    marginLeft,
    marginRight,
    logo,
    emp,
    docTitle: LABELS.budgetSummary,
    docRef: `${idsPresupuestos.length} items`,
    docDate: fmtBudgetDate(new Date()),
    palette,
  });

  y = drawPartyBlock(doc, {
    marginLeft,
    contentW,
    y,
    title: LABELS.client,
    name: dash(header?.name),
    detailLines: buildClientDetailLines(header),
  });

  drawSectionTitle(doc, {
    x: marginLeft,
    y: y + 0.5,
    title: LABELS.lineItems,
    rgb: palette.brand,
  });
  y += 6;

  const head = [
    [
      LABELS.picture,
      LABELS.budgetNo,
      LABELS.quotationDate,
      LABELS.styleName,
      LABELS.unitPrice,
      LABELS.currency,
      LABELS.qty,
    ],
  ];
  const body = [];
  const imgCells = [];

  for (let i = 0; i < idsPresupuestos.length; i += 1) {
    const idPresupuesto = idsPresupuestos[i];
    let local = proyectos.find((p) => p.IdPresupuesto === idPresupuesto);
    let apiDetail = null;
    let totals = {};

    try {
      const apiData = await getImpresionPresupuesto(idPresupuesto);
      const details = Array.isArray(apiData.details) ? apiData.details : [];
      apiDetail = details[0] || {};
      totals = apiData.totals || {
        subTotal: details.reduce((s, l) => s + (parseFloat(l.Venta) || 0), 0),
      };
      if (totals.total == null && Number.isFinite(Number(totals.subTotal))) {
        const units = Number(
          local?.Units ?? apiDetail.TotalUnits ?? apiDetail.Units ?? 0,
        );
        totals.total =
          units > 0 ? Number(totals.subTotal) * units : Number(totals.subTotal);
      }
    } catch (err) {
      console.error("[printBudgetGroupedPdf] detalle:", err);
    }

    const merged = { ...apiDetail, ...local };
    const units = resolveQty(merged, { unidades: local?.Units });
    const unitPrice = resolveUnitPrice(
      merged,
      { precioUnitario: local?.VentaUN, unidades: local?.Units },
      totals,
    );
    const moneda = resolveCurrency(merged);

    const imgData = await resolveBudgetImage(merged, idPresupuesto);
    const rowIndex = body.length;
    body.push([
      imgData ? "" : "—",
      resolveDocRef(merged, idPresupuesto),
      fmtBudgetDate(
        merged?.FechaAceptacion || merged?.FechaPresentacion || null,
      ),
      resolveStyleName(merged),
      fmtBudgetNum(unitPrice),
      moneda,
      fmtBudgetQty(units),
    ]);
    if (imgData) imgCells.push({ row: rowIndex, imgData });

    if (merged.Notas && String(merged.Notas).trim()) {
      const cleanNote = String(merged.Notas)
        .replace(/[\uFFFD\uFEFF]/g, "")
        .trim();
      body.push([
        {
          content: `Notes: ${cleanNote}`,
          colSpan: 7,
          styles: {
            halign: "left",
            valign: "top",
            fillColor: palette.rowAlt,
            textColor: [60, 66, 74],
            fontStyle: "normal",
            fontSize: 8,
            cellPadding: { top: 3.5, bottom: 3.5, left: 6, right: 4 },
          },
        },
      ]);
    }
  }

  const imgTargetH = 26;
  const imgTargetW = 26;

  doc.autoTable({
    head,
    body,
    startY: y,
    theme: "plain",
    margin: { left: marginLeft, right: marginRight },
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.2, right: 1.5, bottom: 2.2, left: 1.5 },
      lineColor: palette.border,
      lineWidth: 0.15,
      textColor: [38, 44, 52],
      valign: "middle",
      halign: "center",
    },
    headStyles: {
      fillColor: palette.tableHead,
      textColor: palette.tableHeadText,
      fontStyle: "bold",
      fontSize: 7.5,
    },
    bodyStyles: {
      minCellHeight: imgTargetH,
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: imgTargetW + 2, halign: "center" },
      1: { cellWidth: 24 },
      2: { cellWidth: 26 },
      3: { cellWidth: 42, halign: "left" },
      4: { halign: "right" },
      6: { halign: "right" },
    },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        const imgCell = imgCells.find((c) => c.row === data.row.index);
        if (imgCell?.imgData) {
          const cell = data.cell;
          const fmt = getImageFormatFromDataUrl(imgCell.imgData);
          const imgX = cell.x + (cell.width - imgTargetW) / 2;
          const imgY = cell.y + (cell.height - imgTargetH) / 2;
          doc.addImage(
            imgCell.imgData,
            fmt,
            imgX,
            imgY,
            imgTargetW,
            imgTargetH,
          );
        }
      }
    },
  });

  applyFooters(doc, emp, palette, marginLeft, marginRight);
  const filename = "Stage_Budget.pdf";
  if (returnBlob) {
    const blob = doc.output("blob");
    return new File([blob], filename, { type: "application/pdf" });
  }
  doc.save(filename);
  return null;
}
